import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@frontend-platform/ui-lib/ui';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from '@tanstack/react-router';
import { ArrowLeft, ArrowRight, Check, ChevronRight } from 'lucide-react';
import { useMemo, useState } from 'react';
import { ROUTES } from '@/config/routes';
import { notifyHostObservationRunPoll } from '@/shared/lib/host-polling-bridge';
import { createCalibrationRun } from '../../api/api';
import { useCalibrationConfig } from '../../hooks/use-calibration-config';
import { useCalibrationRuns } from '../../hooks/use-calibration-runs';
import { useCreateRunForm } from '../../hooks/use-create-run-form';
import {
  CALIBRATION_CONFIG_DEFAULTS,
  type CalibrationConfig,
  getConversationUpperLimit,
} from '../../lib/calibration-config';
import { QK } from '../../lib/query-keys';
import { STEPPER_CONFIG } from './constants';
import { getLiveRunTimeRange, getUtcOffsetStr } from './scope/utils';
import { ConfigureStep } from './steps/configure-step';
import { CriteriaStep } from './steps/criteria-step';
import { EnvironmentStep } from './steps/environment-step';
import { ScopeStep } from './steps/scope-step';
import { type CreateRunFormData, LIVE_START_MODE, STEP_KEYS } from './types';

// stripMs: drop the .SSS millisecond suffix from an ISO string before
// submitting (matches legacy behaviour — backend rejects sub-second precision).
function stripMs(iso: string | null): string | null {
  if (!iso) return iso;
  return iso.replace(/\.\d{3}Z?$/, (m) => (m.endsWith('Z') ? 'Z' : ''));
}

export function buildSubmitPayload(
  formData: CreateRunFormData,
  isLive: boolean,
  // Defaults so tests and any non-federated caller stay simple; real callers
  // pass the bot's own config off @frontend-platform/host-state, since the caps this payload
  // enforces must match the ones the host enforces.
  config: CalibrationConfig = CALIBRATION_CONFIG_DEFAULTS
) {
  // isLive is exactly ScopeStep's own gate (isLiveEnv(evaluationEnvironment))
  // between LiveScope and HistoricalScope — HistoricalScope's own
  // historicalEnvironment can only ever hold a non-prod value
  // (useCalibrationEnvironments' nonProdEnvironmentOptions), so !isLive here
  // means the same "non-prod data source" historical-scope.tsx's banner
  // already computed. Previously this always used the prod cap regardless,
  // so a non-prod run could submit a count above the limit the banner had
  // just told the user applied.
  const cappedConversationCount = Math.min(
    Number(formData.selectedConversationCount) || 0,
    getConversationUpperLimit(!isLive, config)
  );

  const {
    isLoadingConversationCount: _ignored1,
    liveStartMode,
    liveStartISO: _ignored2,
    liveEndISO: _ignored3,
    evaluationManifestId,
    ...rest
  } = formData;

  // A live run is a SCHEDULED run server-side: it listens to traffic over a
  // future window rather than replaying history, so the backend needs both the
  // runType discriminator and the sampling/timezone envelope to schedule it.
  // Historical runs carry neither. Mirrors aistudio's handleCreateRun.
  const liveFields = isLive
    ? (() => {
        // Recomputed here rather than reusing the Scope step's liveStartISO:
        // in NOW mode that value ages while the user finishes the remaining
        // steps, which would submit a start time already in the past.
        const { startTime, endTime } = getLiveRunTimeRange(
          liveStartMode === LIVE_START_MODE.LATER,
          formData.startTime,
          formData.endTime
        );
        return {
          startTime,
          endTime,
          runType: 'SCHEDULED' as const,
          scheduledDataPayload: {
            sampleRate: Number(formData.sampleRate),
            timeZone: getUtcOffsetStr(),
          },
        };
      })()
    : {
        startTime: stripMs(formData.startTime),
        endTime: stripMs(formData.endTime),
      };

  return {
    ...rest,
    // EP bots pin the run to the evaluated env's active build. Non-EP bots have
    // no manifest, so the key is omitted rather than sent as null.
    ...(evaluationManifestId ? { evaluationManifestId } : {}),
    ...liveFields,
    conversationCountThreshold: config.conversationLowerLimit,
    // A live run has no user-selected count — the Scope step only collects a
    // sample rate — so it submits the production cap as the ceiling the
    // backend samples against, matching aistudio.
    selectedConversationCount: isLive ? config.conversationUpperLimit : cappedConversationCount,
  };
}

export function CreateRunPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [discardOpen, setDiscardOpen] = useState(false);

  const { calibrationConfig } = useCalibrationConfig();
  const { data: existingRuns } = useCalibrationRuns();
  const existingRunNames = useMemo(
    () => (existingRuns ?? []).map((run) => run.runName),
    [existingRuns]
  );
  const form = useCreateRunForm({ existingRunNames });
  const {
    formData,
    setField,
    currentStepIndex,
    currentStepKey,
    isLastStep,
    isLiveEnvironment,
    isNameTaken,
    isStepValid,
    nextTooltip,
    goToNext,
    goToPrevious,
    isCompleted,
    isActive,
  } = form;

  // currentStepIndex is always clamped to STEPPER_CONFIG bounds by goToNext/Previous.
  // biome-ignore lint/style/noNonNullAssertion: STEPPER_CONFIG always has at least one entry
  const currentStepEntry = STEPPER_CONFIG[currentStepIndex] ?? STEPPER_CONFIG[0]!;

  const createMutation = useMutation({
    mutationFn: async () => {
      const payload = buildSubmitPayload(formData, isLiveEnvironment, calibrationConfig);
      return createCalibrationRun(payload, {
        env: formData.evaluationEnvironment,
        botRefId: formData.evaluationBotRefId ?? undefined,
      });
    },
    onSuccess: () => {
      // Start the host's polling banner for the run just created, matching
      // aistudio's `appBus.$emit(APP_EVENTS.OBSERVATION_RUN, true)` after a
      // successful create. `true` because the user initiated this run and
      // should see it through to the completion banner.
      notifyHostObservationRunPoll({ showBannerOnCompletion: true });
      // Without this, the list re-mounts serving its staleTime: 30_000 cache,
      // so the run just created wouldn't appear for up to 30s.
      queryClient.invalidateQueries({ queryKey: QK.calibration.runs.all });
      navigate({ to: ROUTES.HOME });
    },
  });

  const handleNextClick = () => {
    if (isLastStep) {
      createMutation.mutate();
      return;
    }
    goToNext();
  };

  const renderStep = () => {
    switch (currentStepKey) {
      case STEP_KEYS.CRITERIA:
        return <CriteriaStep formData={formData} setField={setField} />;
      case STEP_KEYS.ENVIRONMENT:
        return <EnvironmentStep formData={formData} setField={setField} />;
      case STEP_KEYS.SCOPE:
        return (
          <ScopeStep
            formData={formData}
            setField={setField}
            isLiveEnvironment={isLiveEnvironment}
          />
        );
      case STEP_KEYS.CONFIGURE:
        return <ConfigureStep formData={formData} setField={setField} isNameTaken={isNameTaken} />;
      default:
        return null;
    }
  };

  const subHeading =
    currentStepKey === STEP_KEYS.SCOPE && isLiveEnvironment
      ? (currentStepEntry.subHeadingLive ?? currentStepEntry.subHeading)
      : currentStepEntry.subHeading;

  return (
    <div className="flex h-full flex-col overflow-hidden">
      {/* Breadcrumb */}
      <div className="mb-6 flex items-center gap-2 text-sm">
        <Link to={ROUTES.HOME} className="text-muted-foreground hover:text-foreground">
          Calibration
        </Link>
        <ChevronRight className="h-4 w-4 text-muted-foreground" />
        <span className="text-foreground">Create Run</span>
      </div>

      {/* Working area */}
      <div className="flex-1 overflow-hidden rounded-md bg-muted p-6">
        <div className="mx-auto flex h-full max-w-5xl flex-col gap-4">
          {/* Stepper — no connector lines between steps, per design */}
          <div className="mx-auto flex items-center justify-center gap-3">
            {STEPPER_CONFIG.map((step, index) => {
              const Icon = step.icon;
              const done = isCompleted(index);
              const active = isActive(index);
              return (
                <div key={step.key} className="flex w-48 flex-col items-center gap-2">
                  <span
                    className={[
                      'flex size-8 items-center justify-center rounded-full border',
                      done
                        ? 'border-neutral-900 bg-neutral-900 text-white'
                        : active
                          ? 'border-neutral-900 bg-background text-foreground'
                          : 'border-border bg-background text-muted-foreground',
                    ].join(' ')}
                  >
                    {done ? <Check size={20} /> : <Icon size={active ? 20 : 16} />}
                  </span>
                  <span
                    className={[
                      'font-medium text-sm',
                      active || done ? 'text-foreground' : 'text-muted-foreground',
                    ].join(' ')}
                  >
                    {step.label}
                  </span>
                </div>
              );
            })}
          </div>

          {/* Step content card */}
          <div className="flex flex-1 flex-col overflow-hidden rounded-lg border border-border bg-background px-6 py-4 shadow-xs">
            <div className="mb-6 flex flex-col gap-2">
              <h2 className="font-bold text-2xl text-foreground">{currentStepEntry.heading}</h2>
              <p className="text-muted-foreground text-sm leading-[14px]">{subHeading}</p>
            </div>

            <div className="flex-1 overflow-y-auto">{renderStep()}</div>

            <div className="my-4 h-px bg-border" />

            {/* Footer */}
            <div className="flex items-center justify-between">
              <Button variant="ghost" onClick={() => setDiscardOpen(true)}>
                Discard
              </Button>
              <div className="flex items-center gap-3">
                <Button variant="outline" onClick={goToPrevious} disabled={currentStepIndex === 0}>
                  <ArrowLeft className="mr-1 h-3 w-3" /> Previous
                </Button>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span>
                        <Button
                          onClick={handleNextClick}
                          disabled={!isStepValid || createMutation.isPending}
                        >
                          {isLastStep
                            ? createMutation.isPending
                              ? 'Creating…'
                              : 'Create Run'
                            : 'Next'}
                          <ArrowRight className="ml-1 h-3 w-3" />
                        </Button>
                      </span>
                    </TooltipTrigger>
                    {!isStepValid ? <TooltipContent>{nextTooltip}</TooltipContent> : null}
                  </Tooltip>
                </TooltipProvider>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Discard confirmation — gap-2 between title/body to mirror the
          legacy ConfirmationModal layout; default DialogContent gap is too
          loose for this short two-line body. */}
      <Dialog open={discardOpen} onOpenChange={setDiscardOpen}>
        <DialogContent className="max-w-md gap-2 p-6">
          <DialogHeader className="space-y-1 text-left sm:text-left">
            <DialogTitle className="text-left font-medium text-base text-foreground">
              Discard run creation?
            </DialogTitle>
          </DialogHeader>
          <p className="text-muted-foreground text-sm">Your progress will not be saved.</p>
          <DialogFooter className="mt-4 flex justify-end gap-2 sm:justify-end">
            <Button variant="outline" onClick={() => setDiscardOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={() => {
                setDiscardOpen(false);
                navigate({ to: ROUTES.HOME });
              }}
            >
              Discard
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {createMutation.isError ? (
        <p className="mt-2 text-red-600 text-sm">
          Failed to create run. {String(createMutation.error)}
        </p>
      ) : null}
    </div>
  );
}
